import { Component, OnInit } from '@angular/core';
import {
  CandidateDataLoadState,
  CandidateListResultDetailsDto,
  CandidateMandateDistributionDto,
  CandidateResultDto,
  CountStatusDto,
  DetailClient,
  ElectionAreaNavigationItemDto,
  ElectionClient,
  ElectionDto,
  ElectionType,
  ElectoralCoalitionDto,
  ExportDataClient,
  FileResponse,
  ListCoalitionResultDto,
  ListMandateDistributionDto,
  OverviewClient,
  ResultSummaryDto,
  ResultType,
  SideNavigationLinkDto,
  VotingDetailsDto
} from '@shared/clients/api.generated';
import { SidenavigationLink } from '@shared/components/sidenavigation/sidenavigation.model';
import { SvgIcon } from '@valg-shared/utils/svg-icon';
import { StatusPollingDistrict, StatusPollingDistrictColumnDef } from './municipal.model';
import { TopCandidates } from '@shared/components/top-candidates/top-candidates.component';
import { CountStatus } from '@shared/components/count-status/count-status.component';
import {
  PartyOrCandidateListBase,
  PartyOrCandidateListDetail,
} from '@shared/components/expandable-party-list/expandable-party-table.model';
import { VotingDetails } from '@shared/components/voting-details/voting-details.model';
import { SidenavigationLinkType } from '@shared/components/page-layout/page-layout.model';
import { ReferendumStatus } from '@shared/components/cards/referendum-status/referendum-status.model';
import { ActivatedRoute } from "@angular/router";
import { MandateCandidateListItem } from '@shared/components/cards/candidate-mandates/candidate-mandates.model';
import { MandatesList } from '@shared/components/cards/mandates-list/mandates-list.model';
import { saveFile } from "@valg-shared/utils/file-utils";
import { Observable } from "rxjs";
import { SpinnerService } from "@valg-shared/services/spinner/spinner.service";
import { getMapAreaStyle } from "@shared/components/map/map-styles.model";
import { TopCandidatesService } from "@shared/services/top-candidates.service";
import { ExportFileService } from "@valg-shared/services/export-file/export-file.service";
import { ElectionOverviewService } from 'app/pages/election-overview/election-overview.service';
import { sortElectoralCoalitionMembers } from 'utils/sort-utils';

@Component({
  selector: 'valg-dk-details-municipal',
  templateUrl: './municipal.component.html',
  providers: [ SpinnerService ]
})
export class DetailsMunicipalComponent implements OnInit {

  election?: ElectionDto;
  municipality = "";
  ElectionType = ElectionType;
  candidateLists: PartyOrCandidateListBase[] = [];

  statusPollingDistrictColumnDef = StatusPollingDistrictColumnDef;
  statusPollingDistrict: StatusPollingDistrict[] = [];
  candidateDataLoadState: CandidateDataLoadState = CandidateDataLoadState.NotLoaded;

  votingDetails?: VotingDetails;
  resultType: ResultType = ResultType.NoResults;

  readonly SvgIcon = SvgIcon;

  links: SidenavigationLink[] = [];
  filterLinks: SidenavigationLink[] = [];
  sideNavTitleType = SidenavigationLinkType.PollingDistrict;
  parentLinkType = SidenavigationLinkType.Municipality;

  countStatus: CountStatus[] = [];
  countStatusUpdated: Date | undefined;

  referendumStatus: ReferendumStatus[] = [];
  topCandidates: TopCandidates[] = [];

  showPartiesTable = true;
  showReferendumStatus = true;

  coalitionList?: ListCoalitionResultDto[] = [];

  electoralCoalitions?: ElectoralCoalitionDto[] = [];

  candidateMandates: MandateCandidateListItem[] = [];
  mandatesList: MandatesList[] = [];
  resultSummary!: ResultSummaryDto;
  mandateDistributionBasedOnMissingResults = false;

  municipalityId: string;
  regionId?: string;
  isRegionElection = false;
  constructor(
    route: ActivatedRoute,
    private readonly electionClient: ElectionClient,
    private readonly exportDataClient: ExportDataClient,
    private readonly spinner: SpinnerService,
    private readonly detailClient: DetailClient,
    private readonly overviewClient: OverviewClient,
    private readonly topCandidatesService: TopCandidatesService,
    private readonly exportFileService: ExportFileService,
    private readonly electionOverviewService: ElectionOverviewService
  ) {
    this.municipalityId = route.snapshot.params['municipalityId'];
    this.regionId = route.snapshot.params['regionId'];
  }

  ngOnInit(): void {
    this.checkContentAuthorization();
    this.initialize();
  }

  private setCandidateLists(result: CandidateListResultDetailsDto[]) {
    this.candidateLists = result.map((details) => {
      return {
        title: details.displayName,
        votes: this.resultType == ResultType.NoResults ? "" : details.numberOfVotes,
        diff: details.votingDifferencePreviousElection,
        percentage: details.percentages,
        percentage_diff: details.differencePercentages,
        listOrganisation: details.listOrganisationType,
        id: crypto.randomUUID(),
        children: details.candidateResultListWithListVotes.map((cr) => {
          return {
            name: cr.displayName,
            votes: cr.numberOfVotes,
            id: crypto.randomUUID()
          } as PartyOrCandidateListDetail
        })
      } as PartyOrCandidateListBase
    })
  }

  private getMunicipalityDetail() {
    this.detailClient.getMunicipalityDetails(this.municipalityId).subscribe(({
        countStatusDto,
        resultType,
        candidateListResultDetailsList,
        listMandateDistributionDtos,
        electionAreaDetailDto,
        candidateMandateDistributionList,
        electoralCoalitionList,
        votingDetails,
        listCoalitionResults,
        candidateResultDtos,
        candidateDataLoadState,
        resultSummaryDto,
        mandateDistributionBasedOnMissingResults
      }) => {
      this.resultType = resultType;
      this.candidateDataLoadState = candidateDataLoadState;
      this.resultSummary = resultSummaryDto;
      this.setVoteCountStatus(countStatusDto);
      this.setCandidateLists(candidateListResultDetailsList);
      this.setListMandate(listMandateDistributionDtos);
      this.setElectionAreaDetail(electionAreaDetailDto);
      this.setCandidateMandate(candidateMandateDistributionList);
      this.setElectoralCoalitionList(electoralCoalitionList);
      this.setVotingDetails(votingDetails);
      this.setListCoalitionResults(listCoalitionResults);
      this.setTopCandidates(candidateResultDtos);
      this.mandateDistributionBasedOnMissingResults = mandateDistributionBasedOnMissingResults;
    })
  }

  private setVoteCountStatus(voteCountStatusDto: CountStatusDto) {
    this.municipality = voteCountStatusDto.name;
    this.countStatusUpdated = voteCountStatusDto.lastUpdate;
    this.countStatus = [
      {
        name: "count_status.voting_percentage",
        percentage: voteCountStatusDto.percentage?.votingPercentage
      },
      {
        name: "count_status.voting_prec_count_percentage",
        percentage: voteCountStatusDto.percentage?.preCountPercentage
      },
      {
        name: "count_status.voting_fine_count_percentage",
        percentage: voteCountStatusDto.percentage?.fineCountPercentage
      }
    ];
  }

  private setVotingDetails(votingDetailOutsideDto?: VotingDetailsDto) {
    this.votingDetails = {
      numberOfVoters: votingDetailOutsideDto?.numberOfEligibleVoters ?? 0,
      countedPollingAreas: {
        number: votingDetailOutsideDto?.pollingAreasCount?.numberOfPollingAreasHavePreliminaryCount ?? 0,
        total: votingDetailOutsideDto?.pollingAreasCount?.totalPollingAreas ?? 0,
      },
      validVotes: {
        value: votingDetailOutsideDto?.validVotesCount.validVotesTotal ?? 0,
        diff: votingDetailOutsideDto?.validVotesCount.changeComparedToPreviousElection
      },
      invalidVotes: {
        value: votingDetailOutsideDto?.invalidVotesCount.invalidVotesTotal ?? 0,
        diff: votingDetailOutsideDto?.invalidVotesCount.changeComparedToPreviousElection
      },
      blankVotes: {
        value: votingDetailOutsideDto?.blankVotesCount.blankVotesTotal ?? 0,
        diff: votingDetailOutsideDto?.blankVotesCount.changeComparedToPreviousElection
      },
      totalVotes: {
        value: votingDetailOutsideDto?.totalVotesCount.totalVotes ?? 0,
        diff: votingDetailOutsideDto?.totalVotesCount.totalChangeComparedToPreviousElection
      }
    }
  }

  onDownloadReportClick() {
    const handler = this.getExportDataCsv();
    this.spinner.attachSpinner(handler).subscribe(result => {
      const fileName = this.exportFileService.getReportFileNameWithDate(result.fileName, ".csv");
      saveFile(result.data, fileName);
    });
  }
  private getExportDataCsv(): Observable<FileResponse> {
    const currentElection = this.election;
    switch (currentElection?.electionType) {
      case ElectionType.MunicipalElection:
        return this.exportDataClient.exportKvDataCsv(currentElection?.id, this.municipalityId,null);
      case ElectionType.RegionalElection:
        return this.exportDataClient.exportRvDataCsv(currentElection?.id, null, this.municipalityId, null);
      default:
        throw new Error('Election type is not found.');
    }
  }

  initialize() {
    this.electionClient.getCurrentElection().subscribe((result) => {
      this.isRegionElection = result.electionType === ElectionType.RegionalElection;
      this.election = {
        "id": result.id,
        "electionName": result.electionName,
        "electionDate": result.electionDate,
        "electionType": result.electionType,
        "title": result.electionType === ElectionType.Referendum ? result.referendumTitle : undefined
      };
      this.initializeSideNavMenu();
      this.checkReferendumStatusAuthorization();
      this.getMunicipalityDetail();
    });
  }

  private initializeSideNavMenu() {
    this.overviewClient.getMunicipalitySideMenu(this.municipalityId).subscribe((result: SideNavigationLinkDto[]) => {
      if (this.election?.electionType === ElectionType.MunicipalElection) {
        this.links = this.electionOverviewService.mapMunicipality(result, this.municipalityId);
      } else if (this.election?.electionType === ElectionType.RegionalElection) {
        this.links = this.electionOverviewService.mapRegionalMunicipality(result, this.regionId!, this.municipalityId);
      }
    });

    this.overviewClient.getElectionSideNavigation().subscribe((result) => {
      if (this.election?.electionType === ElectionType.MunicipalElection) {
        this.filterLinks = this.electionOverviewService.mapMunicipalSideNavigation(result);
      } else if (this.election?.electionType === ElectionType.RegionalElection) {
        this.filterLinks = this.electionOverviewService.mapRegionalSideNavigation(result);
      }
    });
  }


  checkContentAuthorization(){
    this.checkPartiesTableAuthorization();
    this.checkReferendumStatusAuthorization();
  }

  checkReferendumStatusAuthorization(){
    if(this.election){
      let authorizedElections: ElectionType[] = [
        ElectionType.Referendum
      ];

      this.showReferendumStatus = authorizedElections.includes(this.election?.electionType);
    }
  }

  checkPartiesTableAuthorization(){
    if(this.election){
      let authorizedElections: ElectionType[] = [
        ElectionType.GeneralElection,
        ElectionType.RegionalElection,
        ElectionType.MunicipalElection,
        ElectionType.EuropeanParliamentElection
      ];

      this.showPartiesTable = authorizedElections.includes(this.election?.electionType);
    }
  }

  private setListMandate(result?: ListMandateDistributionDto[]) {
    this.mandatesList = result ? result.map((listMandate) => {
      return {
        id: listMandate.id,
        candidateList: listMandate.displayName,
        numberOfMandates: listMandate.numberOfSeats
      }
    }) : []
  }

  private setElectionAreaDetail(result: ElectionAreaNavigationItemDto[]) {
    this.statusPollingDistrict = result.map((detail) => {
      return {
        id: detail.id,
        nominationDistrict: detail.name,
        status: getMapAreaStyle(detail.resultType)
      }
    })
  }

  private setCandidateMandate(result: CandidateMandateDistributionDto[]) {
    this.candidateMandates = result.map((candidate) => {
      return {
        id: candidate.id,
        candidate: candidate.displayName,
        numberOfMandates: candidate.mandateNumber
      }
    })
  }

  private setElectoralCoalitionList(result?: ElectoralCoalitionDto[]) {
     this.electoralCoalitions = result ? result.map((coalition) => {
      return {
        id: coalition.id,
        listLettersOrName: sortElectoralCoalitionMembers(coalition.listLettersOrName)
      }
    }) : undefined;
  }

  private setListCoalitionResults(result?: ListCoalitionResultDto[]) {
    this.coalitionList = result ? result.map((list) => {
      return {
        id: list.id,
        listCoalitionLetter: list.listCoalitionLetter,
        candidateLettersList: list.candidateLettersList
      }
    }) : undefined
  }

  private setTopCandidates(result: CandidateResultDto[]) {
    this.topCandidates = this.topCandidatesService.fromCandidateResults(result);
  }
}
